import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Camera, Users, MessageCircle, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CurrencyIcon } from "./CountryCurrency";

// Simple Mode hook with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  useSimpleMode = require("@/contexts/SimpleModeContext").useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

export interface NavItem {
  id: string;
  icon: LucideIcon | "country-currency";
  badge?: number;
  isCenter?: boolean;
}

export interface BottomNavProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  activeTab,
  onTabChange,
  className,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSimpleMode } = useSimpleMode();
  const [tappedId, setTappedId] = useState<string | null>(null);

  const navItems: Array<NavItem & { path: string }> = [
    { id: "home", icon: Home, path: "/" },
    { id: "agent", icon: MessageCircle, path: "/agent" },
    { id: "capture", icon: Camera, path: "/camera", isCenter: true },
    { id: "network", icon: Users, path: "/network" },
    { id: "score", icon: "country-currency", path: "/score" },
  ];

  const handleNavigation = (item: typeof navItems[0]) => {
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }
    
    // Tap animation
    setTappedId(item.id);
    setTimeout(() => setTappedId(null), 150);
    
    navigate(item.path);
    onTabChange(item.id);
  };

  // Icon sizes based on mode
  const iconSize = isSimpleMode ? 32 : 24;
  const centerIconSize = isSimpleMode ? 36 : 28;

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "bg-card border-t border-border shadow-lg",
        "pb-[max(env(safe-area-inset-bottom),8px)]",
        className
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-around h-20 px-2 gap-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || activeTab === item.id;
          const isTapped = tappedId === item.id;
          const Icon = item.icon === "country-currency" ? null : item.icon;

          // Center camera button - elevated design
          if (item.isCenter) {
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={cn(
                  "relative flex items-center justify-center",
                  "w-[72px] h-[72px] -mt-6",
                  "rounded-full",
                  "bg-primary text-primary-foreground",
                  "shadow-lg shadow-primary/30",
                  "transition-all duration-150",
                  isTapped && "scale-110",
                  "active:scale-95"
                )}
                aria-label="Camera"
              >
                {Icon && (
                  <Icon
                    size={centerIconSize}
                    strokeWidth={2}
                  />
                )}
                {/* Pulse ring when active */}
                {isActive && (
                  <span className="absolute inset-0 rounded-full bg-primary-foreground/20 animate-ping" />
                )}
              </button>
            );
          }

          // Regular nav items
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              className={cn(
                "relative flex flex-col items-center justify-center",
                "min-w-[64px] min-h-[64px] w-16 h-16",
                "rounded-xl",
                "transition-all duration-150",
                isTapped && "scale-110",
                "active:scale-95",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
              aria-label={item.id}
              aria-current={isActive ? "page" : undefined}
            >
              <div className="relative flex flex-col items-center gap-1.5">
                {item.icon === "country-currency" ? (
                  <CurrencyIcon 
                    size={iconSize + 4}
                    className={cn(
                      "transition-all",
                      isActive ? "opacity-100" : "opacity-60"
                    )}
                  />
                ) : Icon ? (
                  <Icon
                    size={iconSize}
                    fill={isActive ? "currentColor" : "none"}
                    strokeWidth={isActive ? 0 : 2}
                    className="transition-all"
                  />
                ) : null}
                
                {/* Badge */}
                {item.badge && item.badge > 0 && (
                  <Badge
                    size="sm"
                    variant="danger"
                    className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs font-bold"
                  >
                    {item.badge > 9 ? "9+" : item.badge}
                  </Badge>
                )}
                
                {/* Active indicator dot */}
                {isActive && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
